import { Upload01, CheckCircle, XCircle, Trash01, Plus, ArrowLeft } from "@untitledui/icons";
import { FileIcon as FileTypeIcon } from "@untitledui/file-icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { ProgressBar } from "@/components/base/progress-indicators/progress-indicators";
import { useTranslation } from "react-i18next";
import { useDocumentTypes } from "@/hooks/api/use-document-types";
import type { UploadFileItem } from "./file-upload-preview-modal";

interface FileUploadPreviewInlineProps {
    files: UploadFileItem[];
    onFilesChange: (files: UploadFileItem[]) => void;
    onAddFiles: () => void;
    onUpload: () => void;
    onRemoveFile: (fileId: string) => void;
    onViewContent?: () => void;
    isUploading: boolean;
    /** Hide the upload button (upload triggered externally) */
    hideUploadButton?: boolean;
}

const getFileIconType = (fileName: string): string => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    switch (extension) {
        case "pdf":
            return "pdf";
        case "xlsx":
            return "xlsx";
        case "xls":
            return "xls";
        case "csv":
            return "csv";
        case "docx":
            return "docx";
        case "doc":
            return "doc";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "webp":
            return "image";
        case "zip":
            return "zip";
        case "rar":
            return "rar";
        case "7z":
            return "7z";
        case "txt":
            return "txt";
        case "rtf":
            return "rtf";
        case "ppt":
        case "pptx":
            return "powerpoint";
        default:
            return "generic";
    }
};

const getReadableFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 KB";
    const suffixes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.floor(bytes / Math.pow(1024, i)) + " " + suffixes[i];
};

export const FileUploadPreviewInline = ({
    files,
    onFilesChange,
    onAddFiles,
    onUpload,
    onRemoveFile,
    onViewContent,
    isUploading,
    hideUploadButton = false,
}: FileUploadPreviewInlineProps) => {
    const { t, i18n } = useTranslation("pages:documents");
    const { data: documentTypes } = useDocumentTypes();

    const handleNameChange = (fileId: string, newName: string) => {
        onFilesChange(
            files.map((f) => (f.id === fileId ? { ...f, customName: newName } : f))
        );
    };

    const handleDocumentTypeChange = (fileId: string, documentType: string) => {
        onFilesChange(
            files.map((f) => (f.id === fileId ? { ...f, documentType } : f))
        );
    };

    // Build document type options for select
    const documentTypeOptions = [
        { id: "", label: t("drive.upload.preview.unknownDocument") },
        ...(documentTypes?.map((dt) => {
            // Use localized name based on current language
            const localizedName = i18n.language === "it" ? dt.name_it : i18n.language === "en" ? dt.name_en : dt.name;
            return {
                id: dt.key,
                label: localizedName || dt.name,
            };
        }) || []),
    ];

    const canUpload = files.length > 0 && files.every((f) => f.customName.trim() !== "") && !isUploading;
        const allCompleted = files.length > 0 && files.every((f) => f.status === "completed" || f.status === "error");

    if (files.length === 0) {
        return null;
    }

    return (
        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary space-y-4 mb-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-primary">
                    {t("drive.upload.preview.title", { count: files.length })}
                </h2>
            </div>

            {/* File List */}
            <div className="space-y-3">
                {files.map((fileItem) => {
                    const iconType = getFileIconType(fileItem.file.name) as any;
                    const isPending = fileItem.status === "pending";
                    const isUploadingFile = fileItem.status === "uploading";
                    const isCompleted = fileItem.status === "completed";
                    const isError = fileItem.status === "error";

                    return (
                        <div
                            key={fileItem.id}
                            className="rounded-lg bg-secondary p-4 ring-1 ring-border-primary"
                        >
                            <div className="flex items-start gap-4">
                                {/* File Icon */}
                                <div className="shrink-0">
                                    <FileTypeIcon
                                        className="size-10"
                                        type={iconType}
                                        theme="light"
                                        variant="solid"
                                    />
                                </div>

                                {/* File Info */}
                                <div className="flex-1 min-w-0 space-y-3">
                                    {/* Name Input */}
                                    <Input
                                        label={t("drive.upload.preview.fileName")}
                                        value={fileItem.customName}
                                        onChange={(value) => handleNameChange(fileItem.id, value)}
                                        isDisabled={isUploadingFile || isCompleted}
                                        placeholder={fileItem.file.name}
                                    />

                                    {/* Document Type Select */}
                                    <Select.ComboBox
                                        label={t("drive.upload.preview.documentType")}
                                        selectedKey={fileItem.documentType || ""}
                                        onSelectionChange={(key) => handleDocumentTypeChange(fileItem.id, key as string)}
                                        isDisabled={isUploadingFile || isCompleted}
                                        items={documentTypeOptions}
                                        placeholder={t("drive.upload.preview.searchDocumentType", { defaultValue: "Search document type..." })}
                                        shortcut={false}
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item id={item.id} textValue={item.label}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select.ComboBox>

                                    {/* File Size */}
                                    <div className="text-sm text-tertiary">
                                        {getReadableFileSize(fileItem.file.size)}
                                    </div>

                                    {/* Progress Bar */}
                                    {(isUploadingFile || isCompleted) && (
                                        <div className="space-y-1">
                                            <ProgressBar
                                                value={fileItem.progress}
                                                min={0}
                                                max={100}
                                            />
                                            {isUploadingFile && (
                                                <div className="text-xs text-tertiary">
                                                    {fileItem.progress}%
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Status Icons and Messages */}
                                    <div className="flex items-center gap-2">
                                        {isPending && (
                                            <>
                                                <Upload01 className="size-4 text-tertiary" />
                                                <span className="text-sm text-tertiary">
                                                    {t("drive.upload.preview.status.pending")}
                                                </span>
                                            </>
                                        )}
                                        {isUploadingFile && (
                                            <>
                                                <Upload01 className="size-4 text-brand-600 animate-pulse" />
                                                <span className="text-sm text-brand-600">
                                                    {t("drive.upload.preview.status.uploading")}
                                                </span>
                                            </>
                                        )}
                                        {isCompleted && (
                                            <>
                                                <CheckCircle className="size-4 text-success-600" />
                                                <span className="text-sm text-success-600 font-medium">
                                                    {t("drive.upload.preview.status.completed")}
                                                </span>
                                            </>
                                        )}
                                        {isError && (
                                            <>
                                                <XCircle className="size-4 text-error-primary" />
                                                <span className="text-sm text-error-primary font-medium">
                                                    {fileItem.error || t("drive.upload.preview.status.error")}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Remove Button */}
                                {!isUploadingFile && (
                                    <Button
                                        color="tertiary"
                                        size="sm"
                                        onClick={() => onRemoveFile(fileItem.id)}
                                        iconLeading={<Trash01 className="size-4" />}
                                        className="shrink-0"
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-border-primary">
                {allCompleted && onViewContent ? (
                    <Button
                        color="primary"
                        size="md"
                        onClick={onViewContent}
                        iconLeading={<ArrowLeft className="size-4" />}
                    >
                        {t("drive.upload.preview.viewContent")}
                    </Button>
                ) : (
                    <Button
                        color="secondary"
                        size="md"
                        onClick={onAddFiles}
                        iconLeading={<Plus className="size-4" />}
                        isDisabled={isUploading}
                    >
                        {t("drive.upload.preview.addMore")}
                    </Button>
                )}
                {!hideUploadButton && (
                    <div className="flex gap-2">
                        {!allCompleted && (
                            <Button
                                color="primary"
                                size="md"
                                onClick={onUpload}
                                isDisabled={!canUpload}
                                iconLeading={isUploading ? undefined : <Upload01 className="size-4" />}
                            >
                                {isUploading
                                    ? t("drive.upload.preview.uploading")
                                    : t("drive.upload.preview.upload")}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

